/**
 * Idle scheduling helpers.
 *
 * Everything that is not part of the first meaningful paint (consent banner,
 * push prompt, language nudge, route prefetching) is deferred until the main
 * thread is free, so it never competes with the first render.
 */

type IdleHandle = { cancel: () => void };

export function runAfterIdle(task: () => void, timeout = 2000): IdleHandle {
  if (typeof window === "undefined") return { cancel: () => {} };

  const ric = (window as Window & {
    requestIdleCallback?: (cb: IdleRequestCallback, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (id: number) => void;
  }).requestIdleCallback;

  if (typeof ric === "function") {
    const id = ric(() => task(), { timeout });
    return {
      cancel: () => (window as unknown as { cancelIdleCallback?: (i: number) => void })
        .cancelIdleCallback?.(id),
    };
  }

  const id = window.setTimeout(task, Math.min(timeout, 800));
  return { cancel: () => window.clearTimeout(id) };
}

/** React-friendly: true once the browser has gone idle after first paint. */
export function onIdleOnce(task: () => void, timeout = 2000) {
  let done = false;
  const handle = runAfterIdle(() => {
    if (done) return;
    done = true;
    task();
  }, timeout);
  return () => {
    done = true;
    handle.cancel();
  };
}

/**
 * Runs a task only once the page has finished painting its largest contentful
 * element (or the user interacts, or a hard cap elapses).
 *
 * `requestIdleCallback` fires very early on throttled mobile CPUs — often
 * while the LCP text is still waiting for its web font — so background work
 * scheduled on idle alone competes with the visible content for bandwidth.
 * This gate keeps the same deferral mechanism but anchors it to LCP instead.
 */
export function runAfterLcp(task: () => void, hardCapMs = 6000): IdleHandle {
  if (typeof window === "undefined") return { cancel: () => {} };

  let done = false;
  let settleTimer = 0;
  let observer: PerformanceObserver | undefined;

  const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "touchstart"];

  const cleanup = () => {
    window.clearTimeout(settleTimer);
    window.clearTimeout(capTimer);
    observer?.disconnect();
    events.forEach((e) => window.removeEventListener(e, fire));
  };

  function fire() {
    if (done) return;
    done = true;
    cleanup();
    task();
  }

  const capTimer = window.setTimeout(fire, hardCapMs);
  events.forEach((e) => window.addEventListener(e, fire, { once: true, passive: true }));

  const settle = () => {
    window.clearTimeout(settleTimer);
    // A web-font swap produces a fresh LCP entry; wait for the stream to
    // quieten before treating the paint as final.
    settleTimer = window.setTimeout(fire, 700);
  };

  try {
    observer = new PerformanceObserver(settle);
    observer.observe({ type: "largest-contentful-paint", buffered: true });
    settle();
  } catch {
    return runAfterIdle(fire, 2000);
  }

  return { cancel: () => { done = true; cleanup(); } };
}
